import React, { useEffect, useState } from "react";
import "./VideoList.scss";
import { BASE_URL } from "../services/constant";
import { fetchContentAll } from "../services/apiCategorie";
import { fetchUsers } from "../services/apiUsers";
import {
  deleteCaptionDetails,
  fetchCaptionTracks,
  fetchCatalogContentDetail,
  fetchEpisodeDetailForEdit,
  fetchPublicationCollections,
  fetchSeriesPublicationTree,
  updateCaptionDetails,
  updateCatalogContent,
  updateEpisodeDetail,
  updatePublicationContent,
  updatePublicationEpisode,
  updateSeasonDetails,
  uploadCaptionTrack,
} from "../services/apiCatalogAdmin";

const TYPE_TABS = [
  { id: "films", label: "Films" },
  { id: "serialized", label: "Series & docs" },
];

const BUCKET_TABS = [
  { id: "unpublished", label: "Contenus a publier" },
  { id: "published", label: "Contenus publies" },
];

const MATURITY_OPTIONS = ["", "7+", "10+", "13+", "16+", "18+"];

function limitText(text, max = 120) {
  if (!text) return "";
  return text.length > max ? `${text.substring(0, max)}...` : text;
}

function formatDuration(seconds) {
  if (!seconds || Number.isNaN(Number(seconds))) return "00:00";
  const safe = Number(seconds);
  const h = Math.floor(safe / 3600);
  const m = Math.floor((safe % 3600) / 60);
  const s = Math.floor(safe % 60);

  if (!h) {
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }

  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function buildStudioImage(path) {
  const apiRoot = String(BASE_URL || "").replace(/\/+$/, "");
  if (!path) return `${apiRoot}/images/bg.png`;

  const cleanPath = String(path).trim().replace(/\\/g, "/");
  if (/^https?:\/\//i.test(cleanPath)) {
    return cleanPath;
  }

  if (cleanPath.startsWith("/uploads") || cleanPath.startsWith("/api/")) {
    return `${apiRoot}${cleanPath}`;
  }

  if (cleanPath.startsWith("uploads")) {
    return `${apiRoot}/${cleanPath}`;
  }

  if (cleanPath.startsWith("/media/")) {
    return `${apiRoot}/api${cleanPath}`;
  }

  if (cleanPath.startsWith("media/")) {
    return `${apiRoot}/api/${cleanPath}`;
  }

  return `${apiRoot}/api/${cleanPath.replace(/^\/+/, "")}`;
}

function initialEditorState() {
  return {
    open: false,
    targetType: null,
    item: null,
    form: {},
  };
}

function initialCaptionState() {
  return {
    open: false,
    targetType: "content",
    targetId: null,
    targetLabel: "",
    tracks: [],
    form: {
      label: "",
      language_code: "fr",
      kind: "subtitles",
      is_default: true,
      caption: null,
    },
  };
}

function VideoListPub() {
  const [search, setSearch] = useState("");
  const [bucket, setBucket] = useState("unpublished");
  const [typeTab, setTypeTab] = useState("films");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingIds, setSavingIds] = useState({});
  const [expandedSeriesId, setExpandedSeriesId] = useState(null);
  const [seriesTree, setSeriesTree] = useState(null);
  const [selectedSeasonId, setSelectedSeasonId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [editor, setEditor] = useState(initialEditorState());
  const [captionModal, setCaptionModal] = useState(initialCaptionState());

  useEffect(() => {
    async function loadBaseData() {
      try {
        const [categoriesData, usersData] = await Promise.all([fetchContentAll(), fetchUsers()]);
        setCategories(categoriesData || []);
        setUsers(usersData || []);
      } catch (error) {
        console.error("Erreur chargement base admin:", error);
      }
    }

    loadBaseData();
  }, []);

  const loadItems = async () => {
    try {
      setLoading(true);
      const data = await fetchPublicationCollections(bucket);
      setItems(data || []);
    } catch (error) {
      console.error("Erreur chargement publications:", error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
    setExpandedSeriesId(null);
    setSeriesTree(null);
    setSelectedSeasonId(null);
  }, [bucket]);

  const filtered = items.filter((item) => {
    const matchesType = typeTab === "films" ? item.type === "film" : item.type === "serie" || item.type === "documentaire";
    const searchable = `${item.title || ""} ${item.description || ""} ${item.category_title || ""}`.toLowerCase();
    return matchesType && searchable.includes(search.toLowerCase());
  });

  const activeSeason =
    seriesTree?.seasons?.find((season) => Number(season.id) === Number(selectedSeasonId)) || seriesTree?.seasons?.[0] || null;

  const markSaving = (key, value) => {
    setSavingIds((current) => ({ ...current, [key]: value }));
  };

  const refreshSeriesTree = async (seriesId) => {
    const data = await fetchSeriesPublicationTree(seriesId);
    setSeriesTree(data);
    setSelectedSeasonId((current) => {
      if (data?.seasons?.some((season) => Number(season.id) === Number(current))) {
        return current;
      }
      return data?.seasons?.[0]?.id || null;
    });
  };

  const toggleSeriesPanel = async (seriesId) => {
    if (expandedSeriesId === seriesId) {
      setExpandedSeriesId(null);
      setSeriesTree(null);
      setSelectedSeasonId(null);
      return;
    }

    setExpandedSeriesId(seriesId);
    await refreshSeriesTree(seriesId);
  };

  const handleToggleContentPublication = async (item) => {
    const key = `content-${item.id}`;
    try {
      markSaving(key, true);
      await updatePublicationContent(item.id, Number(item.statutpub || 0) === 2 ? 1 : 2);
      await loadItems();
      if (expandedSeriesId === item.id) {
        await refreshSeriesTree(item.id);
      }
    } catch (error) {
      console.error(error);
      alert(error?.response?.data?.error || "Erreur lors de la mise a jour de la publication.");
    } finally {
      markSaving(key, false);
    }
  };

  const handleToggleEpisodePublication = async (episode) => {
    const key = `episode-${episode.id}`;
    try {
      markSaving(key, true);
      await updatePublicationEpisode(episode.id, Number(episode.statutpub || 0) === 2 ? 1 : 2);
      if (seriesTree?.collection?.id) {
        await refreshSeriesTree(seriesTree.collection.id);
      }
      await loadItems();
    } catch (error) {
      console.error(error);
      alert(error?.response?.data?.error || "Erreur lors de la publication de l'episode.");
    } finally {
      markSaving(key, false);
    }
  };

  const openContentEditor = async (item) => {
    try {
      const detail = await fetchCatalogContentDetail(item.id);
      setEditor({
        open: true,
        targetType: "content",
        item: detail,
        form: {
          title: detail?.title || "",
          type: detail?.type || "film",
          description: detail?.description || "",
          categorie_id: detail?.categorie_id ? String(detail.categorie_id) : "",
          user_id: detail?.user_id ? String(detail.user_id) : "",
          maturity_rating: detail?.maturity_rating || "",
          video_img_1: null,
          video_img_2: null,
        },
      });
    } catch (error) {
      console.error(error);
      alert("Impossible de charger cette fiche.");
    }
  };

  const openSeasonEditor = (season) => {
    setEditor({
      open: true,
      targetType: "season",
      item: season,
      form: {
        title: season?.title || "",
        season_number: season?.season_number ? String(season.season_number) : "",
      },
    });
  };

  const openEpisodeEditor = async (episodeId) => {
    try {
      const detail = await fetchEpisodeDetailForEdit(episodeId);
      setEditor({
        open: true,
        targetType: "episode",
        item: detail,
        form: {
          title: detail?.title || "",
          description: detail?.description || "",
          episode_number: detail?.episode_number ? String(detail.episode_number) : "1",
          duration: detail?.duration ? String(detail.duration) : "0",
          season_id: detail?.season_id ? String(detail.season_id) : detail?.season_number ? String(detail.season_number) : "",
          season_number: detail?.season_number ? String(detail.season_number) : "",
          maturity_rating: detail?.maturity_rating || "",
        },
      });
    } catch (error) {
      console.error(error);
      alert("Impossible de charger cet episode.");
    }
  };

  const closeEditor = () => setEditor(initialEditorState());

  const handleEditorField = (field, value) => {
    setEditor((current) => ({
      ...current,
      form: {
        ...current.form,
        [field]: value,
      },
    }));
  };

  const submitEditor = async (event) => {
    event.preventDefault();

    try {
      if (editor.targetType === "content") {
        const formData = new FormData();
        Object.entries(editor.form).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== "") {
            formData.append(key, value);
          }
        });
        await updateCatalogContent(editor.item.id, formData);
      } else if (editor.targetType === "season") {
        await updateSeasonDetails(editor.item.id, {
          title: editor.form.title,
          season_number: editor.form.season_number,
        });
      } else if (editor.targetType === "episode") {
        await updateEpisodeDetail(editor.item.id, {
          title: editor.form.title,
          description: editor.form.description,
          episode_number: editor.form.episode_number,
          duration: editor.form.duration,
          season_id: editor.form.season_id,
          season_number: editor.form.season_number,
          maturity_rating: editor.form.maturity_rating,
        });
      }

      closeEditor();
      await loadItems();
      if (expandedSeriesId) {
        await refreshSeriesTree(expandedSeriesId);
      }
    } catch (error) {
      console.error(error);
      alert(error?.response?.data?.error || "Erreur lors de la mise a jour.");
    }
  };

  const openCaptionManager = async (targetType, targetId, targetLabel) => {
    try {
      const tracks = await fetchCaptionTracks(targetType, targetId);
      setCaptionModal({
        open: true,
        targetType,
        targetId,
        targetLabel,
        tracks,
        form: {
          label: "",
          language_code: "fr",
          kind: "subtitles",
          is_default: tracks.length === 0,
          caption: null,
        },
      });
    } catch (error) {
      console.error(error);
      alert("Impossible de charger les captions.");
    }
  };

  const closeCaptionManager = () => setCaptionModal(initialCaptionState());

  const handleCaptionField = (field, value) => {
    setCaptionModal((current) => ({
      ...current,
      form: {
        ...current.form,
        [field]: value,
      },
    }));
  };

  const refreshCaptions = async () => {
    const tracks = await fetchCaptionTracks(captionModal.targetType, captionModal.targetId);
    setCaptionModal((current) => ({
      ...current,
      tracks,
      form: {
        ...current.form,
        caption: null,
      },
    }));
  };

  const submitCaption = async (event) => {
    event.preventDefault();

    if (!captionModal.form.caption) {
      alert("Selectionnez un fichier .vtt ou .srt.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("label", captionModal.form.label);
      formData.append("language_code", captionModal.form.language_code);
      formData.append("kind", captionModal.form.kind);
      formData.append("is_default", captionModal.form.is_default ? "1" : "0");
      formData.append("caption", captionModal.form.caption);
      await uploadCaptionTrack(captionModal.targetType, captionModal.targetId, formData);
      await refreshCaptions();
    } catch (error) {
      console.error(error);
      alert(error?.response?.data?.error || "Erreur lors de l'upload du caption.");
    }
  };

  const markCaptionAsDefault = async (captionId) => {
    try {
      await updateCaptionDetails(captionId, { is_default: 1 });
      await refreshCaptions();
    } catch (error) {
      console.error(error);
      alert("Impossible de definir ce caption par defaut.");
    }
  };

  const removeCaption = async (captionId) => {
    try {
      await deleteCaptionDetails(captionId);
      await refreshCaptions();
    } catch (error) {
      console.error(error);
      alert("Impossible de supprimer ce caption.");
    }
  };

  return (
    <div className="page-content">
      <div className="video-list-container video-list-container--pub">
        <div className="video-list-header">
          <div>
            <h2 className="page-title">Publication catalogue</h2>
            <p className="page-subtitle">
              Publication des films, series et documentaires, avec controle independant sur chaque episode.
            </p>
          </div>
        </div>

        <div className="tabs">
          {BUCKET_TABS.map((tab) => (
            <button key={tab.id} className={`tab ${bucket === tab.id ? "active" : ""}`} onClick={() => setBucket(tab.id)}>
              {tab.label}
            </button>
          ))}
        </div>

        <div className="tabs tabs--secondary">
          {TYPE_TABS.map((tab) => (
            <button key={tab.id} className={`tab ${typeTab === tab.id ? "active" : ""}`} onClick={() => setTypeTab(tab.id)}>
              {tab.label}
            </button>
          ))}
        </div>

        <div className="filter-bar">
          <input
            type="text"
            placeholder="Rechercher un titre..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        {loading ? <div className="video-list-empty">Chargement des contenus studio...</div> : null}

        {!loading && typeTab === "films" ? (
          <table className="video-table">
            <thead>
              <tr>
                <th>Video</th>
                <th>Type</th>
                <th>Statut</th>
                <th>Categorie</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((video) => (
                <tr key={video.id}>
                  <td className="video-cell">
                    <div className="thumb">
                      <img src={buildStudioImage(video.video_img_1 || video.video_img_2 || video.image)} alt={video.title} />
                      <span className="duration">{formatDuration(video.duration)}</span>
                    </div>
                    <div className="video-info">
                      <strong>{video.title}</strong>
                      <p>{limitText(video.description, 75)}</p>
                    </div>
                  </td>
                  <td>{video.type}</td>
                  <td>
                    <span className={`status-pill ${Number(video.statutpub || 0) === 2 ? "is-published" : "is-draft"}`}>
                      {Number(video.statutpub || 0) === 2 ? "Publie" : "Studio"}
                    </span>
                  </td>
                  <td>{video.category_title || "-"}</td>
                  <td>{formatDate(video.created_at)}</td>
                  <td>
                    <div className="actions-inline">
                      <button
                        className={`pub-btn ${Number(video.statutpub || 0) === 2 ? "published" : "unpublished"}`}
                        disabled={Boolean(savingIds[`content-${video.id}`])}
                        onClick={() => handleToggleContentPublication(video)}
                      >
                        {Number(video.statutpub || 0) === 2 ? "Retirer" : "Publier"}
                      </button>
                      <button className="ghost-btn" onClick={() => openContentEditor(video)}>
                        Modifier
                      </button>
                      <button className="ghost-btn" onClick={() => openCaptionManager("content", video.id, video.title)}>
                        Captions
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}

        {!loading && typeTab === "serialized" ? (
          
          <div className="publication-cards">
            {filtered.map((item) => {
              const isExpanded = expandedSeriesId === item.id;
              return (
                <article key={item.id} className={`publication-card ${isExpanded ? "is-expanded" : ""}`}>
                  <div className="publication-card__hero">
                    <img src={buildStudioImage(item.video_img_1 || item.video_img_2 || item.image)} alt={item.title} />
                    <div className="publication-card__overlay" />
                    <div className="publication-card__content">
                      <span className="publication-card__type">
                        {item.type === "documentaire" ? "Documentaire" : "Serie"}
                      </span>
                      <h3>{item.title}</h3>
                      <p>{limitText(item.description, 150)}</p>
                      <div className="publication-card__meta">
                        <span>{item.category_title || "-"}</span>
                        <span>{item.seasons_count || 0} saisons</span>
                        <span>{item.episodes_count || 0} episodes</span>
                        <span>{item.published_episodes_count || 0} episodes publies</span>
                      </div>
                      <div className="publication-card__actions">
                        <button
                          className={`pub-btn ${Number(item.statutpub || 0) === 2 ? "published" : "unpublished"}`}
                          disabled={Boolean(savingIds[`content-${item.id}`])}
                          onClick={() => handleToggleContentPublication(item)}
                        >
                          {Number(item.statutpub || 0) === 2 ? "Retirer la fiche" : "Publier la fiche"}
                        </button>
                        <button className="ghost-btn" onClick={() => toggleSeriesPanel(item.id)}>
                          {isExpanded ? "Masquer episodes" : "Voir episodes"}
                        </button>
                        <button className="ghost-btn" onClick={() => openContentEditor(item)}>
                          Modifier
                        </button>
                        <button className="ghost-btn" onClick={() => openCaptionManager("content", item.id, item.title)}>
                          Captions fiche
                        </button>
                      </div>
                    </div>
                  </div>

                  {isExpanded && seriesTree?.collection?.id === item.id ? (
                    <div className="publication-card__details">
                      <div className="publication-card__detailHeader">
                        <div>
                          <p className="eyebrow">Lecture continue</p>
                          <h4>Saisons et episodes</h4>
                        </div>
                        {activeSeason ? (
                          <button className="ghost-btn" onClick={() => openSeasonEditor(activeSeason)}>
                            Modifier la saison
                          </button>
                        ) : null}
                      </div>

                      <div className="season-pill-row">
                        {seriesTree.seasons.map((season) => (
                          <button
                            key={season.id}
                            className={`season-pill ${Number(activeSeason?.id) === Number(season.id) ? "is-active" : ""}`}
                            onClick={() => setSelectedSeasonId(season.id)}
                          >
                            Saison {season.season_number || season.id}
                          </button>
                        ))}
                      </div>

                      <div className="episode-grid">
                        {(activeSeason?.episodes || []).map((episode) => (
                          <article key={episode.id} className="episode-studio-card">
                            <div className="episode-studio-card__media">
                              <img src={buildStudioImage(episode.image || item.video_img_1 || item.video_img_2)} alt={episode.title} />
                              <span className="episode-studio-card__duration">{formatDuration(episode.duration)}</span>
                            </div>
                            <div className="episode-studio-card__body">
                              <div className="episode-studio-card__header">
                                <div>
                                  <span className="episode-studio-card__index">
                                    Episode {episode.episode_number}
                                  </span>
                                  <h5>{episode.title}</h5>
                                </div>
                                <span className={`status-pill ${Number(episode.statutpub || 0) === 2 ? "is-published" : "is-draft"}`}>
                                  {Number(episode.statutpub || 0) === 2 ? "Publie" : Number(episode.statutpub || 0) === 1 ? "Pret" : "Studio"}
                                </span>
                              </div>
                              <p>{limitText(episode.description, 120) || "Episode pret a etre complete ou publie."}</p>
                              <div className="actions-inline">
                                <button
                                  className={`pub-btn ${Number(episode.statutpub || 0) === 2 ? "published" : "unpublished"}`}
                                  disabled={Boolean(savingIds[`episode-${episode.id}`])}
                                  onClick={() => handleToggleEpisodePublication(episode)}
                                >
                                  {Number(episode.statutpub || 0) === 2 ? "Retirer" : "Publier"}
                                </button>
                                <button className="ghost-btn" onClick={() => openEpisodeEditor(episode.id)}>
                                  Modifier
                                </button>
                                <button className="ghost-btn" onClick={() => openCaptionManager("episode", episode.id, episode.title)}>
                                  Captions
                                </button>
                              </div>
                            </div>
                          </article>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        ) : null}

        {!loading && !filtered.length ? <div className="video-list-empty">Aucun contenu correspondant.</div> : null}
      </div>

      {editor.open ? (
        <div className="admin-modal" onClick={closeEditor}>
          <div className="admin-modal__dialog" onClick={(event) => event.stopPropagation()}>
            <div className="admin-modal__header">
              <h3>
                {editor.targetType === "content"
                  ? "Modifier la fiche"
                  : editor.targetType === "season"
                    ? "Modifier la saison"
                    : "Modifier l'episode"}
              </h3>
              <button className="ghost-btn" onClick={closeEditor}>
                Fermer
              </button>
            </div>

            <form className="admin-modal__form" onSubmit={submitEditor}>
              {editor.targetType === "content" ? (
                <>
                  <label>
                    Titre
                    <input value={editor.form.title} onChange={(event) => handleEditorField("title", event.target.value)} />
                  </label>
                  <label>
                    Type
                    <select value={editor.form.type} onChange={(event) => handleEditorField("type", event.target.value)}>
                      <option value="film">Film</option>
                      <option value="serie">Serie</option>
                      <option value="documentaire">Documentaire</option>
                    </select>
                  </label>
                  <label className="admin-modal__full">
                    Description
                    <textarea rows="5" value={editor.form.description} onChange={(event) => handleEditorField("description", event.target.value)} />
                  </label>
                  <label>
                    Categorie
                    <select value={editor.form.categorie_id} onChange={(event) => handleEditorField("categorie_id", event.target.value)}>
                      <option value="">Selectionner</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.title}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Proprietaire
                    <select value={editor.form.user_id} onChange={(event) => handleEditorField("user_id", event.target.value)}>
                      <option value="">Selectionner</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.nom}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Classification
                    <select value={editor.form.maturity_rating} onChange={(event) => handleEditorField("maturity_rating", event.target.value)}>
                      {MATURITY_OPTIONS.map((option) => (
                        <option key={option || "none"} value={option}>
                          {option || "Aucune"}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Affiche principale
                    <input type="file" accept="image/*" onChange={(event) => handleEditorField("video_img_1", event.target.files?.[0] || null)} />
                  </label>
                  <label>
                    Affiche secondaire
                    <input type="file" accept="image/*" onChange={(event) => handleEditorField("video_img_2", event.target.files?.[0] || null)} />
                  </label>
                </>
              ) : null}

              {editor.targetType === "season" ? (
                <>
                  <label>
                    Titre de la saison
                    <input value={editor.form.title} onChange={(event) => handleEditorField("title", event.target.value)} />
                  </label>
                  <label>
                    Numero
                    <input value={editor.form.season_number} onChange={(event) => handleEditorField("season_number", event.target.value)} />
                  </label>
                </>
              ) : null}

              {editor.targetType === "episode" ? (
                <>
                  <label>
                    Titre
                    <input value={editor.form.title} onChange={(event) => handleEditorField("title", event.target.value)} />
                  </label>
                  <label>
                    Numero episode
                    <input value={editor.form.episode_number} onChange={(event) => handleEditorField("episode_number", event.target.value)} />
                  </label>
                  <label>
                    Duree (secondes)
                    <input value={editor.form.duration} onChange={(event) => handleEditorField("duration", event.target.value)} />
                  </label>
                  <label>
                    Classification
                    <select value={editor.form.maturity_rating} onChange={(event) => handleEditorField("maturity_rating", event.target.value)}>
                      {MATURITY_OPTIONS.map((option) => (
                        <option key={option || "none"} value={option}>
                          {option || "Aucune"}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="admin-modal__full">
                    Description
                    <textarea rows="5" value={editor.form.description} onChange={(event) => handleEditorField("description", event.target.value)} />
                  </label>
                </>
              ) : null}

              <div className="admin-modal__actions">
                <button type="button" className="ghost-btn" onClick={closeEditor}>
                  Annuler
                </button>
                <button type="submit" className="pub-btn unpublished">
                  Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {captionModal.open ? (
        <div className="admin-modal" onClick={closeCaptionManager}>
          <div className="admin-modal__dialog admin-modal__dialog--wide" onClick={(event) => event.stopPropagation()}>
            <div className="admin-modal__header">
              <div>
                <h3>Captions</h3>
                <p className="page-subtitle">{captionModal.targetLabel}</p>
              </div>
              <button className="ghost-btn" onClick={closeCaptionManager}>
                Fermer
              </button>
            </div>

            <div className="caption-manager">
              <form className="admin-modal__form" onSubmit={submitCaption}>
                <label>
                  Libelle
                  <input value={captionModal.form.label} onChange={(event) => handleCaptionField("label", event.target.value)} placeholder="Francais, English..." />
                </label>
                <label>
                  Code langue
                  <input value={captionModal.form.language_code} onChange={(event) => handleCaptionField("language_code", event.target.value)} placeholder="fr, en..." />
                </label>
                <label>
                  Type
                  <select value={captionModal.form.kind} onChange={(event) => handleCaptionField("kind", event.target.value)}>
                    <option value="subtitles">Subtitles</option>
                    <option value="captions">Captions / CC</option>
                    <option value="forced">Forced</option>
                  </select>
                </label>
                <label className="checkbox-line">
                  <input
                    type="checkbox"
                    checked={Boolean(captionModal.form.is_default)}
                    onChange={(event) => handleCaptionField("is_default", event.target.checked)}
                  />
                  Caption par defaut
                </label>
                <label className="admin-modal__full">
                  Fichier .vtt ou .srt
                  <input type="file" accept=".vtt,.srt" onChange={(event) => handleCaptionField("caption", event.target.files?.[0] || null)} />
                </label>
                <div className="admin-modal__actions">
                  <button type="submit" className="pub-btn unpublished">
                    Ajouter le caption
                  </button>
                </div>
              </form>

              <div className="caption-list">
                <h4>Pistes existantes</h4>
                {captionModal.tracks.length ? (
                  captionModal.tracks.map((track) => (
                    <div key={track.id} className="caption-item">
                      <div>
                        <strong>{track.label}</strong>
                        <p>
                          {track.language} • {track.kind} {track.isDefault ? "• defaut" : ""}
                        </p>
                      </div>
                      <div className="actions-inline">
                        {!track.isDefault ? (
                          <button className="ghost-btn" onClick={() => markCaptionAsDefault(track.id)}>
                            Defaut
                          </button>
                        ) : null}
                        <button className="ghost-btn ghost-btn--danger" onClick={() => removeCaption(track.id)}>
                          Supprimer
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="video-list-empty">Aucune piste caption pour le moment.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default VideoListPub;
